import React from 'react';
import { Grid, Divider, Box } from '@mui/material';
import { Field, Formik } from 'formik';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { CerebroDrawer, CerebroHeading, CerebroForm, CerebroPrimaryButton, CerebroText, CerebroTextarea } from '../../shared/components';
import { useApi } from '../../shared/context';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import DOMPurify from 'dompurify';


interface SuggestedLendersDrawerProps {
  open: boolean;
  close: () => void;
  dealGuid?: string;
  loading?: boolean;
  initialPrompt?: string;
}

export const SuggestedLendersDrawer: React.FC<SuggestedLendersDrawerProps> = ({
  open,
  close,
  dealGuid,
  loading,
  initialPrompt
}) => {
  const api = useApi();
  const snackbar = useSnackbar();

  const [prompt, setPrompt] = React.useState<string>("");
  const [results, setResults] = React.useState<string>("");

  React.useEffect(() => {
    if (initialPrompt) setPrompt(initialPrompt);
  }, [initialPrompt]);

  const runQuery = useMutation(
    async ({ values }: SuggestedLendersMutationVariables) => {
      const { data, error } = await getApiResponseData<SuggestedLendersResponse>(
        api.post(CerebroApiEndpoints.SuggestedLenders.Root(dealGuid), { customPrompt: values.prompt })
      );

      if (error) {
        throw new Error(error.response?.data ?? "Error fetching lender insights");
      }

      return data;
    },
    {
      onSuccess: (data, { helpers }) => {
        const cleaned = (data?.response ?? "")
        .replace(/^<\s*[^>]*?>/i, "") // removes the first <...> tag
        .trim();
        
        setResults(cleaned);

        helpers.setFieldValue("prompt", data?.prompt ?? "");
      },
      onError: (error: Error) => {
        snackbar.enqueueSnackbar(error.message, { variant: 'error' });
      },
      onSettled: (_data, _error, _variables) => {
        _variables.helpers.setSubmitting(false);
      }
    }
  );

  return (
    <CerebroDrawer open={open} onClose={close} PaperProps={{ sx: { width: '900px' } }}>
      <CerebroHeading size="h1" component="h1">
        Suggested Lenders
      </CerebroHeading>

      <Formik
        onSubmit={FormikUtils.MutateOnSubmit(runQuery.mutate)}
        initialValues={{ prompt }}
      >
        {({ handleSubmit, values, setFieldValue, isSubmitting }) => (
          <CerebroForm onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Field
              id="Prompt"
              as={CerebroTextarea}
              name="prompt"
              rows={10}
              disabled={isSubmitting}
            />
            <CerebroPrimaryButton
              type="submit"
              sx={{ mt: 2 }}
              loading={isSubmitting}
            >
              Ask AI
            </CerebroPrimaryButton>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CerebroHeading size="h2">Results</CerebroHeading>
              </Grid>

              {!results && (
                <Grid item xs={12}>
                  <CerebroText sx={{ color: 'gray' }}>
                    No results yet. Adjust the prompt and click Ask AI.
                  </CerebroText>
                </Grid>
              )}

              {results && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      typography: "body2",
                      "& h2": { fontSize: "1.25rem", fontWeight: 600, mt: 2 },
                      "& h3": { fontSize: "1.1rem", fontWeight: 500, mt: 1.5 },
                      "& ul": { pl: 3 },
                      "& table": { width: "100%", borderCollapse: "collapse", my: 2 },
                      "& th, & td": { border: "1px solid #ddd", p: 1 },
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(results ?? "") }}
                  />
                </Grid>
              )}
            </Grid>
          </CerebroForm>
        )}
      </Formik>
    </CerebroDrawer>
  );
};

type SuggestedLendersMutationVariables = FormikUtils.MutateOnSubmitVariables<SuggestedLendersSearch>;
interface SuggestedLendersSearch {
  prompt: string;
}

interface SuggestedLendersResponse {
  prompt: string;
  response: string;
}